import React from 'react';
import { motion } from 'framer-motion';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  MessageCircle,
  Instagram,
  Twitter,
  Facebook,
} from 'lucide-react';

// Row 1: Office, Email | Row 2: Phone, WhatsApp
const contactDetails = [
  [
    {
      icon: MapPin,
      label: 'Office',
      value: '7 Bell Yard, London, WC2A 2JR',
      href: 'https://maps.google.com/?q=7+Bell+Yard,+London,+WC2A+2JR',
      isExternal: true,
    },
    {
      icon: Mail,
      label: 'Email',
      value: 'buildwealth@novahass.partners',
      href: 'mailto:buildwealth@novahass.partners',
      isExternal: false,
    },
  ],
  [
    {
      icon: Phone,
      label: 'Phone',
      value: '+44 7395 171691',
      href: 'tel:+447395171691',
      isExternal: false,
    },
    {
      icon: MessageCircle,
      label: 'WhatsApp',
      value: '+44 7395 171691',
      href: 'https://wa.me/447395171691',
      isExternal: true,
    },
  ],
];

const businessHours = [
  { days: 'Monday - Friday', hours: '7:00 AM - 7:00 PM' },
  { days: 'Saturday - Sunday', hours: '8:00 AM - 12:00 PM' },
];

const socialLinks = [
  {
    icon: Instagram,
    label: 'Instagram',
    handle: '@novahass.partners',
    href: 'https://instagram.com/novahass.partners',
  },
  {
    icon: Twitter,
    label: 'Twitter',
    handle: '@novahass.partner',
    href: 'https://twitter.com/novahass.partner',
  },
  {
    icon: Facebook,
    label: 'Facebook',
    handle: 'Novahass Partners',
    href: 'https://facebook.com/novahasspartners',
  },
];

export const ContactInfo: React.FC = () => {
  return (
    <section className="py-16 sm:py-20 lg:py-24 relative">
      <div className="container mx-auto px-4 sm:px-6 max-w-5xl">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 bg-luxury-gold/10 border border-luxury-gold/20 rounded-full mb-4"
          >
            <Phone className="w-4 h-4 text-luxury-gold mr-2" />
            <span className="text-sm font-medium text-luxury-gold">Get In Touch</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-bold text-luxury-text dark:text-luxury-text-dark"
          >
            Contact{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-luxury-gold to-luxury-gold-light">
              Information
            </span>
          </motion.h2>
        </div>

        {/* Contact Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Contact Details Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="bg-luxury-surface dark:bg-luxury-elevated rounded-2xl border border-luxury-gold/10 p-6 sm:p-8"
          >
            <h3 className="text-lg font-semibold text-luxury-text dark:text-luxury-text-dark mb-6">
              Reach Us Directly
            </h3>
            <div className="space-y-4">
              {contactDetails.map((row, rowIdx) => (
                <div key={rowIdx} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {row.map((contact, colIdx) => {
                    const Icon = contact.icon;
                    return (
                      <motion.a
                        key={contact.label}
                        href={contact.href}
                        target={contact.isExternal ? '_blank' : undefined}
                        rel={contact.isExternal ? 'noopener noreferrer' : undefined}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + rowIdx * 0.1 + colIdx * 0.05 }}
                        className="group flex items-start gap-3 p-3 rounded-xl hover:bg-luxury-gold/5 transition-all duration-300"
                      >
                        <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-luxury-gold/10 border border-luxury-gold/20 flex items-center justify-center group-hover:bg-luxury-gold/20 transition-colors duration-300">
                          <Icon className="w-4 h-4 text-luxury-gold" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark uppercase tracking-wide mb-0.5">
                            {contact.label}
                          </div>
                          <div className="text-sm font-medium text-luxury-text dark:text-luxury-text-dark group-hover:text-luxury-gold transition-colors duration-300 break-all">
                            {contact.value}
                          </div>
                        </div>
                      </motion.a>
                    );
                  })}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Hours & Social Card */}
          <div className="space-y-6">
            {/* Business Hours */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="bg-luxury-surface dark:bg-luxury-elevated rounded-2xl border border-luxury-gold/10 p-6 sm:p-8"
            >
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-xl bg-luxury-gold/10 border border-luxury-gold/20 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-luxury-gold" />
                </div>
                <h3 className="text-lg font-semibold text-luxury-text dark:text-luxury-text-dark">
                  Business Hours
                </h3>
              </div>
              <div className="space-y-3">
                {businessHours.map((schedule, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between py-2 border-b border-luxury-gold/10 last:border-0"
                  >
                    <span className="text-sm text-luxury-text-muted dark:text-luxury-text-muted-dark">
                      {schedule.days}
                    </span>
                    <span className="text-sm font-medium text-luxury-text dark:text-luxury-text-dark">
                      {schedule.hours}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                  Available for enquiries
                </span>
              </div>
            </motion.div>

            {/* Social Links */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
              className="bg-luxury-surface dark:bg-luxury-elevated rounded-2xl border border-luxury-gold/10 p-6 sm:p-8"
            >
              <h3 className="text-lg font-semibold text-luxury-text dark:text-luxury-text-dark mb-5">
                Follow Us
              </h3>
              <div className="flex flex-col sm:flex-row justify-center gap-3">
                {socialLinks.map((social) => {
                  const Icon = social.icon;
                  return (
                    <a
                      key={social.label}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group flex items-center gap-3 px-4 py-3 rounded-xl bg-luxury-gold/5 border border-luxury-gold/10 hover:border-luxury-gold/30 hover:bg-luxury-gold/10 transition-all duration-300"
                    >
                      <Icon className="w-5 h-5 text-luxury-gold" />
                      <div className="text-left">
                        <div className="text-xs text-luxury-text-muted dark:text-luxury-text-muted-dark">
                          {social.label}
                        </div>
                        <div className="text-sm font-medium text-luxury-text dark:text-luxury-text-dark group-hover:text-luxury-gold transition-colors">
                          {social.handle}
                        </div>
                      </div>
                    </a>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactInfo;
